import React, { type RefObject, useMemo, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import Menu, { MenuItem } from '@amzn/meridian/menu';
import {
  ALTERNATIVE_PLACEMENT_RECOMMENDED,
  APPROVED_TWP,
  ASSOCIATE_DECISION_PENDING,
  DENIED_TWP,
  DLS_REFERRAL_RECEIVED,
  INDEFINITE_RESTRICTIONS,
  PLACEMENT_DECISION_PENDING,
  TWP_BUT_PENDING_UPDATED_RESTRICTIONS,
} from 'src/config/AccommodationStatusNames';
import { restrictionsHaveError } from 'src/hooks/restrictionValidation/restrictionsValidator';
import { useFetchTaskGroups } from 'src/hooks/restrictionValidation/useFetchTaskGroups';
import JmrService from 'src/services/jmr/jmrService';
import { initializeEditAssociateModal } from 'src/store/placementDetailsPageSlice/placementDetailsPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { constructMedicalAttachmentsLink } from 'src/utils/constructRTWLinks';
import { JmrFlowTypes } from 'src/utils/jmrUtils';

import ConfirmUpdateRestrictionsModal from '../pages/placementDetails/confirmUpdateRestrictionsModal/confirmUpdateRestrictionsModal';
import EditAssociateInfoModalAUSTIN from '../pages/placementDetails/editAssociateInfoModal/editAssociateInfoModal';
import InitiateTransferModal from '../pages/placementDetails/initiateTransferModal/initiateTransferModal';
import { type PlacementInfo } from '../pages/placementDetails/updatePlacementStatusModal/constants';
import UpdatePlacementStatusModal from '../pages/placementDetails/updatePlacementStatusModal/updatePlacementStatusModal';

import ConfirmModal from './confirmModal/confirmModal';
import LoadingSpinnerWrapper from './loadingSpinner/loadingSpinnerWrapper';

interface UpdateMenuActions {
  closeMenu: VoidFunction;
  onStatusUpdateSuccess: VoidFunction;
  onTransferSuccess?: VoidFunction;
}

interface PlacementUpdateMenuProps {
  placement: PlacementInfo;
  anchorRef: RefObject<HTMLButtonElement>;
  isMenuOpen: boolean;
  showAdminEdit?: boolean;
  actions: UpdateMenuActions;
}

const PlacementUpdateMenu = ({
  placement,
  anchorRef,
  isMenuOpen,
  showAdminEdit = false,
  actions: { closeMenu, onStatusUpdateSuccess, onTransferSuccess },
}: PlacementUpdateMenuProps) => {
  const {
    placementId,
    regionId,
    accommodationType,
    placementStatus,
    externalCaseId,
    managerAlias,
    fc,
    modifiedShiftLength,
    workRestrictions,
  } = placement;
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { featureFlags, isFeatureFlagsLoaded } = useAppSelector((state) => state.user);

  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isConfirmUpdateRestrictionsModalOpen, setIsConfirmUpdateRestrictionsModalOpen] = useState(false);
  const [isInitiateTransferModalOpen, setIsInitiateTransferModalOpen] = useState(false);
  const [isRestrictionEntriesErrorModalOpen, setIsRestrictionEntriesErrorModalOpen] = useState(false);
  const [isAlternativePlacementModalOpen, setIsAlternativePlacementModalOpen] = useState(false);
  const [isUpdateRestrictionDatesModalOpen, setIsUpdateRestrictionDatesModalOpen] = useState(false);
  const [isUploadRfiModalOpen, setIsUploadRfiModalOpen] = useState(false);

  // load task groups to perform validation. The loading state is a concern of update menu as updates should be blocked
  // until validation is done
  const { isLoading: isLoadingTaskGroups, taskGroups } = useFetchTaskGroups(
    isFeatureFlagsLoaded,
    featureFlags,
    regionId,
  );
  const mustUpdateRestrictions = useMemo(
    () => restrictionsHaveError(workRestrictions, modifiedShiftLength, taskGroups),
    [workRestrictions, modifiedShiftLength, taskGroups],
  );

  // These statuses are allowed to enter the update placement flow
  const STATUSES_ALLOWED_TO_UPDATE_PLACEMENTS = featureFlags.DECISION_PORTAL
    ? [
        PLACEMENT_DECISION_PENDING,
        ASSOCIATE_DECISION_PENDING,
        APPROVED_TWP,
        INDEFINITE_RESTRICTIONS,
        TWP_BUT_PENDING_UPDATED_RESTRICTIONS,
        DLS_REFERRAL_RECEIVED,
        DENIED_TWP,
      ]
    : [PLACEMENT_DECISION_PENDING, ALTERNATIVE_PLACEMENT_RECOMMENDED];

  // Navigate to the update JMR flow, remembering the current page so Cancel can return here
  const navigateToUpdateJmr = (updateType: JmrFlowTypes) => {
    navigate(`/newJMR/${placementId}/update/${updateType}`, { state: { returnTo: location.pathname } });
  };

  // Navigate to Confirm update restrictions from the modal
  const confirmUpdateRestrictions = async () => {
    // Note we rely on the loading spinner of the menu to prevent executing this function before validation completes
    if (placementId && !mustUpdateRestrictions) {
      const id = parseInt(placementId);
      const { data: isUpdateRestrictionAllowed } = await JmrService.getUpdateRestrictionAllowed(id);
      if (isUpdateRestrictionAllowed) {
        navigateToUpdateJmr(JmrFlowTypes.NewRestrictions);
      } else {
        setIsConfirmUpdateRestrictionsModalOpen(false);
        setIsUploadRfiModalOpen(true);
      }
    } else {
      navigateToUpdateJmr(JmrFlowTypes.NewRestrictions);
    }
  };

  const initiateTransferActionClickHandler = () => {
    if (mustUpdateRestrictions) {
      setIsRestrictionEntriesErrorModalOpen(true);
    } else {
      setIsInitiateTransferModalOpen(true);
    }
  };

  const placementSelectionActionClickHandler = () => {
    if (mustUpdateRestrictions) {
      setIsRestrictionEntriesErrorModalOpen(true);
    } else {
      setIsAlternativePlacementModalOpen(true);
    }
  };

  const restrictionDatesActionClickHandler = () => {
    if (mustUpdateRestrictions) {
      setIsRestrictionEntriesErrorModalOpen(true);
    } else {
      setIsUpdateRestrictionDatesModalOpen(true);
    }
  };

  return (
    <>
      <Menu
        anchorNode={anchorRef.current ?? undefined}
        open={isMenuOpen}
        position="bottom"
        onClose={closeMenu}
        data-cy="placement-detail__update-menu"
        minWidth="200px"
      >
        <LoadingSpinnerWrapper showSpinner={isLoadingTaskGroups}>
          {placement.statusOptions.length > 0 && (
            <MenuItem onClick={() => setIsStatusModalOpen(true)} data-cy="placement-details__status-btn">
              Status
            </MenuItem>
          )}
          <MenuItem onClick={restrictionDatesActionClickHandler} data-cy="placement-details__extend-end-date-btn">
            Restriction Dates
          </MenuItem>
          <MenuItem
            onClick={() => setIsConfirmUpdateRestrictionsModalOpen(true)}
            data-cy="placement-details__restrictions-btn"
          >
            Restrictions
          </MenuItem>
          {STATUSES_ALLOWED_TO_UPDATE_PLACEMENTS.includes(placementStatus) && (
            <MenuItem
              onClick={placementSelectionActionClickHandler}
              data-cy="placement-details__alternative-placement-btn"
            >
              Placement Selection(s)
            </MenuItem>
          )}
          {accommodationType === 'AUSTIN' && (
            <MenuItem onClick={initiateTransferActionClickHandler} data-cy="placement-details__initiate-transfer-btn">
              Initiate Transfer
            </MenuItem>
          )}
          {showAdminEdit && (
            <MenuItem
              onClick={() => dispatch(initializeEditAssociateModal())}
              data-cy="placement-details__admin-edit-btn"
              data-testid="placement-details__admin-edit-btn"
            >
              Admin Edit
            </MenuItem>
          )}
        </LoadingSpinnerWrapper>
      </Menu>
      <ConfirmModal
        modalTitle="Update Placement Restrictions?"
        modalDescription="If restrictions and start/end dates have changed, Restrictions will navigate you to a page where
                you will enter new restrictions and dates."
        open={isConfirmUpdateRestrictionsModalOpen}
        setOpen={(open) => setIsConfirmUpdateRestrictionsModalOpen(open)}
        onConfirm={confirmUpdateRestrictions}
      />
      <ConfirmModal
        modalTitle="Upload RFI or WSR?"
        modalDescription="You must upload a new RFI or WSR to update the restrictions for this placement. If a new RFI or WSR has
          already been uploaded, please wait a few minutes and try again."
        open={isUploadRfiModalOpen}
        setOpen={(open) => setIsUploadRfiModalOpen(open)}
        onConfirm={() => setIsUploadRfiModalOpen(false)}
        includeLink={true}
        linkText="Austin Incident Link"
        linkUrl={externalCaseId && constructMedicalAttachmentsLink(externalCaseId)}
        showCancelButton={false}
      />
      <ConfirmModal
        modalTitle="Select Alternative Placements?"
        modalDescription="If the site decision maker accepted an alternative placement, Placement Selections(s) will navigate you
              to a page where you will select new placements."
        open={isAlternativePlacementModalOpen}
        setOpen={(open) => setIsAlternativePlacementModalOpen(open)}
        onConfirm={() => navigateToUpdateJmr(JmrFlowTypes.AlternativePlacementRecommended)}
      />
      <ConfirmModal
        modalTitle="Update Restriction Dates?"
        modalDescription="If ONLY start/end dates have changed (restrictions have remained the same), Restriction Dates will navigate
              you to a page where you will select new start and end dates."
        open={isUpdateRestrictionDatesModalOpen}
        setOpen={(open) => setIsUpdateRestrictionDatesModalOpen(open)}
        onConfirm={() => navigateToUpdateJmr(JmrFlowTypes.UpdateDates)}
      />
      {onTransferSuccess && (
        <InitiateTransferModal
          placementId={placementId!}
          managerAlias={managerAlias}
          fc={fc}
          isOpen={isInitiateTransferModalOpen}
          closeModal={() => setIsInitiateTransferModalOpen(false)}
          onTransferSuccess={onTransferSuccess}
        />
      )}
      <ConfirmUpdateRestrictionsModal
        open={isRestrictionEntriesErrorModalOpen}
        onClose={() => setIsRestrictionEntriesErrorModalOpen(false)}
        submit={() => navigateToUpdateJmr(JmrFlowTypes.NewRestrictions)}
      />
      {isStatusModalOpen && (
        <UpdatePlacementStatusModal
          open={isStatusModalOpen}
          ctx={{
            placement,

            onSuccess: onStatusUpdateSuccess,
            onClose: () => setIsStatusModalOpen(false),
          }}
        />
      )}
      <EditAssociateInfoModalAUSTIN />
    </>
  );
};

export default PlacementUpdateMenu;
